'use client';

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { MdMoreVert } from 'react-icons/md';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useTranslations } from 'next-intl';
import NextImage from 'next/image';
import { useParams } from 'next/navigation';
import EditListModal from '../../../../components/EditListModal';
import EpisodeItem from '../../../../components/EpisodeItem';
import Loading from '../../../../components/Loading';
import LoadingContainer from '../../../../components/LoadingContainer';
import ResourceItem from '../../../../components/ResourceItem';
import WarningContainer from '../../../../components/WarningContainer';
import WarningText from '../../../../components/WarningText';
import Wrapper from '../../../../components/Wrapper';
import { amplitudeInstance } from '../../../../config/amplitude';
import AuthContext from '../../../../context/AuthContext';
import buildRouteUrl from '../../../../helpers/buildRouteUrl';
import buildTitleUrl from '../../../../helpers/buildTitleUrl';
import useIntl from '../../../../hooks/useIntl';
import { Link, useRouter } from '../../../../i18n/navigation';
import api from '../../../../services/api';
import { BasicResource, Metadata, UserList as UserListType } from '../../../../types/types';
require('dayjs/locale/en');
require('dayjs/locale/pt-br');
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
function ClientList({
  list
}: {
  list: {
    data: UserListType;
  };
}) {
  const params = useParams();
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();
  const router = useRouter();
  const intl = useIntl();
  const t = useTranslations();
  let locale: string;
  const listId = params.id;
  if (intl.full_language === 'en-US') {
    locale = 'en';
  } else {
    locale = intl.full_language.toLowerCase();
  }
  const [isEditListModalVisible, setIsEditListModalVisible] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const containerRef = useRef<any>(null);
  const removeItemsFromListMutation = useMutation({
    mutationFn: async ({
      listId: listId_0,
      contentId,
      contentType
    }: {
      listId: string;
      contentId: string;
      contentType: string;
    }) => {
      const response = await api.delete(`/v1/me/lists/${listId_0}/items`, {
        data: {
          items: [{
            id: contentId,
            type: contentType
          }]
        }
      });
      return response.data;
    },
    onSuccess: (_, {
      listId: listId_1,
      contentId: contentId_0
    }) => {
      toast(t('pages.user_list.messages.succesfully_removed_item.title'), {
        position: 'bottom-center',
        type: 'success'
      });
      if (queryClient.getQueryData([`lists-${listId_1}-items-with-pagination`])) {
        queryClient.setQueryData([`lists-${listId_1}-items-with-pagination`], (old: any) => {
          const pages = old.pages.map((page: any) => {
            return {
              ...page,
              metadata: {
                ...page.metadata,
                items: page.metadata.items - 1,
                total: page.metadata.total - 1
              },
              data: page.data.filter((item: BasicResource) => {
                if (item._id !== contentId_0) {
                  return item;
                }
              })
            };
          });
          return {
            pages,
            pageParams: old.pageParams
          };
        });
      }
      queryClient.invalidateQueries({
        queryKey: [`lists-${listId_1}-items-with-pagination`]
      });
      queryClient.invalidateQueries({
        queryKey: [`user-${auth.userData?._id}-lists-with-pagination`]
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
    },
    onError: (err, __0, rollback: any) => {
      toast(t('pages.user_list.messages.error_removing_item.title'), {
        position: 'bottom-center',
        type: 'error'
      });
    }
  });
  const deleteListMutation = useMutation({
    mutationFn: async ({
      listId: listId_2
    }: {
      listId: string;
    }) => {
      const response_0 = await api.delete(`/v1/me/lists/${listId_2}`);
      return response_0.data;
    },
    onSuccess: (__1, {
      listId: listId_3
    }) => {
      if (queryClient.getQueryData([`users-${auth.userData?._id}`])) {
        queryClient.setQueryData([`users-${auth.userData?._id}`], (old_0: any) => {
          return {
            data: {
              ...old_0.data,
              lists: old_0.data.lists.filter((list_0: UserListType) => list_0._id !== listId_3)
            }
          };
        });
      }
      if (queryClient.getQueryData([`user-${auth.userData?._id}-lists-with-pagination`])) {
        queryClient.setQueryData([`user-${auth.userData?._id}-lists-with-pagination`], (old_1: any) => {
          const pages_0 = old_1.pages.map((page_0: any) => {
            return {
              ...page_0,
              metadata: {
                ...page_0.metadata,
                items: page_0.metadata.items - 1,
                total: page_0.metadata.total - 1
              },
              data: page_0.data.filter((list_1: UserListType) => {
                if (list_1._id !== listId_3) {
                  return list_1;
                }
              })
            };
          });
          return {
            pages: pages_0,
            pageParams: old_1.pageParams
          };
        });
      }
      queryClient.invalidateQueries({
        queryKey: [`lists-${listId_3}`]
      });
      queryClient.invalidateQueries({
        queryKey: [`user-${auth.userData?._id}-lists-with-pagination`]
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      router.back();
    }
  });
  const listQuery = useQuery<{
    data: UserListType;
  }>({
    queryKey: [`lists-${listId}`],
    queryFn: async () => {
      const response_1 = await api.get(`/v1/lists/${listId}`, {
        headers: {
          'accept-language': intl.full_language
        }
      });
      return response_1.data;
    },
    enabled: !!listId,
    initialData: {
      data: list.data
    }
    // onError: (err: any) => {
    //     if (err.response.status === 404) {
    //       if (queryClient.getQueryData(`users-${auth.userData?._id}`)) {
    //         queryClient.setQueryData(
    //           `users-${auth.userData?._id}`,
    //           (old: any) => {
    //             return {
    //               data: {
    //                 ...old.data,
    //                 lists: old.data.lists.filter(
    //                   (list: UserListType) => list._id !== listId
    //                 )
    //               }
    //             };
    //           }
    //         );
    //       }

    //       if (
    //         queryClient.getQueryData(
    //           `user-${auth.userData?._id}-lists-with-pagination`
    //         )
    //       ) {
    //         queryClient.setQueryData(
    //           `user-${auth.userData?._id}-lists-with-pagination`,
    //           (old: any) => {
    //             const pages = old.pages.map((page: any) => {
    //               return {
    //                 ...page,
    //                 metadata: {
    //                   ...page.metadata,
    //                   items: page.metadata.items - 1,
    //                   total: page.metadata.total - 1
    //                 },
    //                 data: page.data.filter((list: UserListType) => {
    //                   if (list._id !== listId) {
    //                     return list;
    //                   }
    //                 })
    //               };
    //             });

    //             return {
    //               pages,
    //               pageParams: old.pageParams
    //             };
    //           }
    //         );
    //       }

    //       queryClient.invalidateQueries(
    //         `user-${auth.userData?._id}-lists-with-pagination`
    //       );
    //       queryClient.invalidateQueries(`users-${auth.userData?._id}`);

    //       router.back();
    //     }
    //   }
  });
  const listItemsQuery = useInfiniteQuery<{
    data: BasicResource[];
    metadata: Metadata;
  }>({
    queryKey: [`lists-${listId}-items-with-pagination`],
    queryFn: async ({
      pageParam = 0
    }) => {
      const offset = pageParam as number || 0;
      const response_2 = await api.get(`/v1/lists/${listId}/items?offset=${offset}&limit=50`, {
        headers: {
          'accept-language': intl.full_language
        }
      });
      return response_2.data;
    },
    initialPageParam: 0,
    getNextPageParam: lastGroup => {
      if (lastGroup.metadata.total > lastGroup.metadata.offset) {
        return lastGroup.metadata.offset + 50;
      }
      return undefined;
    },
    enabled: !!listId
  });
  useEffect(() => {
    if (listItemsQuery.isSuccess) {
      // eslint-disable-next-line no-unused-expressions
      listItemsQuery.data?.pages.forEach(group => {
        setTotalItems(prevState => prevState + group.data.length);
      });
    }
  }, [listItemsQuery.isSuccess, listItemsQuery.data]);
  const onEndReached = useCallback(() => {
    if (listItemsQuery.hasNextPage && !listItemsQuery.isFetchingNextPage) {
      listItemsQuery.fetchNextPage();
    }
  }, [listItemsQuery.hasNextPage, listItemsQuery.isFetchingNextPage]);
  return <Wrapper>
      {listQuery.data?.data && <EditListModal data={listQuery.data?.data} isOpen={isEditListModalVisible} onClose={() => setIsEditListModalVisible(false)} />}
      <BottomScrollListener onBottom={onEndReached} offset={150}>
        {() => <div ref={containerRef} className="flex flex-col w-full bg-gray-600">
            <div className="flex flex-row bg-gray-600 w-full z-[8]">
              {listQuery.isSuccess && listItemsQuery.isSuccess && <div className="w-full bg-black px-4 md:px-10 py-2 flex flex-col border-gray-600 border-y-[1px] border-solid">
                  <div className="flex flex-row justify-between">
                    <div className="flex flex-col gap-y-0.5">
                      <h1 className="text-white text-3xl font-bold mr-2">
                        {listQuery.data?.data?.name}
                      </h1>
                      {listQuery.data?.data?.description && <p className="text-white">
                          {listQuery.data?.data?.description}
                        </p>}
                    </div>
                    {listQuery.data?.data?.owner?._id === auth.userData?._id && <DropdownMenu.Root>
                        <DropdownMenu.Trigger className="flex justify-center items-center w-[30px] h-[30px] rounded-full px-4 text-black bg-white hover:bg-gray-200">
                          <MdMoreVert className="min-w-[18px] h-[18px]" size={18} />
                        </DropdownMenu.Trigger>

                        <DropdownMenu.Portal style={{
                  zIndex: 100
                }} className="z-10">
                          <DropdownMenu.Content align="end" sideOffset={5} className={'radix-side-top:animate-slide-up radix-side-bottom:animate-slide-down w-48 rounded-lg px-1.5 py-1 shadow-md md:w-56 bg-white dark:bg-gray-400 z-10'}>
                            <DropdownMenu.Item className={'flex cursor-default select-none items-center rounded-md px-2 py-2 text-xs outline-none focus:bg-gray-200'}>
                              <span onClick={() => {
                        amplitudeInstance.logEvent('Open Edit List Modal From User List Page', {
                          list_id: listId as string
                        });
                        setIsEditListModalVisible(true);
                      }} className="flex-grow text-white cursor-pointer">
                                {t('pages.user_list.menu.edit')}
                              </span>
                            </DropdownMenu.Item>

                            <DropdownMenu.Item className={'flex cursor-default select-none items-center rounded-md px-2 py-2 text-xs outline-none focus:bg-gray-200'}>
                              <span onClick={() => {
                        if (!deleteListMutation.isPending && listId) {
                          deleteListMutation.mutate({
                            listId: listId as string
                          });
                        }
                      }} className="flex-grow text-white cursor-pointer">
                                {t('pages.user_list.menu.delete')}
                              </span>
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>}
                  </div>
                  <div className="flex flex-col mt-5">
                    <span className="text-gray-300 text-sm">
                      {t('pages.user_list.header.created_by')}
                    </span>
                    <div className="flex flex-row items-center mt-1">
                      <button className="w-[30px] h-[30px] rounded-full bg-gray-600" onClick={() => router.push({
                  pathname: '/users/[id]',
                  params: {
                    id: listQuery.data?.data?.owner?._id
                  }
                })}>
                        {listQuery.data?.data?.owner?.profile_image && <NextImage src={listQuery.data.data.owner.profile_image.versions.find(version => version.resolution === '300x300')?.url as string} alt={t('images_alts.user_image', {
                    name: listQuery.data.data.owner.name
                  })} className="rounded-full mb-4 w-full h-full border-solid border-[2px] border-black bg-gray-600 hover:bg-gray-150" width={30} height={30} unoptimized />}
                      </button>
                      <Link href={{
                  pathname: '/users/[id]',
                  params: {
                    id: listQuery.data?.data?.owner?._id
                  }
                }} className="text-white font-medium ml-2 hover:text-gray-200">
                        {listQuery.data?.data?.owner?.name}
                      </Link>
                    </div>
                    <span className="text-white mt-2 text-sm">
                      {t('pages.user_list.header.last_updated')}{' '}
                      {dayjs(listQuery.data.data.last_updated_at).locale(locale).format('LLL')}
                    </span>
                  </div>
                </div>}
            </div>

            <div>
              {listItemsQuery.isLoading && <LoadingContainer>
                  <Loading />
                </LoadingContainer>}

              {listQuery.isSuccess && listItemsQuery.isSuccess && listItemsQuery.data.pages[0].metadata.total === 0 && <WarningContainer>
                    <WarningText>
                      {t('pages.user_list.warnings.empty_list')}
                    </WarningText>
                  </WarningContainer>}

              {listQuery.isSuccess && listItemsQuery.isSuccess && totalItems > 0 && <div className="custom-grid w-full h-full gap-x-6 gap-y-3 pb-4 mt-4 px-4 md:px-10">
                    {listItemsQuery.data?.pages.map((group_0, groupIndex) => <React.Fragment key={groupIndex}>
                        {group_0.data.map(item_0 => {
                if (item_0.type === 'movie' || item_0.type === 'tv') {
                  return <Link key={item_0._id} href={buildTitleUrl({
                    type: item_0.type,
                    locale: intl.locale.toLowerCase(),
                    shortId: item_0.short_id,
                    slugs: item_0.slugs
                  })} prefetch={false}>
                                <ResourceItem data={item_0} onDelete={listQuery.data?.data?.owner?._id === auth.userData?._id ? () => {
                      if (!removeItemsFromListMutation.isPending && listId) {
                        amplitudeInstance.logEvent('Remove Item From List From User List Page', {
                          list_id: listId as string,
                          content_id: item_0._id,
                          content_type: item_0.type
                        });
                        removeItemsFromListMutation.mutate({
                          listId: listId as string,
                          contentId: item_0._id,
                          contentType: item_0.type
                        });
                      }
                    } : undefined} />
                              </Link>;
                }
                if (item_0.type === 'episode') {
                  return <Link key={item_0._id} href={buildRouteUrl(`/episode/${item_0._id}`)} prefetch={false}>
                                <EpisodeItem key={item_0._id} data={item_0} onDelete={listQuery.data?.data?.owner?._id === auth.userData?._id ? () => {
                      if (!removeItemsFromListMutation.isPending && listId) {
                        amplitudeInstance.logEvent('Remove Item From List From User List Page', {
                          list_id: listId as string,
                          content_id: item_0._id,
                          content_type: item_0.type
                        });
                        removeItemsFromListMutation.mutate({
                          listId: listId as string,
                          contentId: item_0._id,
                          contentType: item_0.type
                        });
                      }
                    } : undefined} />
                              </Link>;
                }
              })}
                      </React.Fragment>)}
                  </div>}
            </div>
          </div>}
      </BottomScrollListener>
    </Wrapper>;
}
export default ClientList;